// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import { connect } from "react-redux";
import React, { useEffect, useCallback } from "react";
import { Portal } from "@chakra-ui/react";

import Heading from "./Heading";
import {
  Content as StyledContent,
  PrimaryButton,
  Overlay,
  ModalText,
  SecondaryFooter,
  HeadingWrapper,
  Secondary,
  Form,
} from "./styles";
import { ModalFormBody } from "src/components/chatroom-nav/filter/styles";
import * as colors from "src/styles/constants/colors";
import { Input } from "src/styles/input";
import {
  getWorkflowInstanceFilter,
  getReport,
  getInstanceReportId,
  getProcessFilterId,
  getProcessTableColumns,
  getExpandedFields,
  getModalReportType,
  getAdditionalFilters,
  getReportModal,
  getExpandedRows,
  getFormFieldsVisibility,
  getAllRecords,
} from "src/reducers";
import {
  createReport,
  hideNewReportsModal,
  reportModalAttributes,
  updateReportTitle,
  deleteReport,
} from "src/actions/reports";
import ButtonLoader from "src/components/ButtonLoader";

import type {
  AppState,
  Report,
  ReportModalType,
  WorkflowInstanceFilter,
  ReportModal,
  ExpandedEmbeddedFields,
  ExpandedEmbeddedRows,
  FormFieldsVisibility,
  AllRecords,
} from "src/types";

type Props = {
  report: ?Report,
  reportModal: ReportModal,
  processColumns: Array<{ key: string, active: boolean }>,
  type: ReportModalType,
  filters: WorkflowInstanceFilter,
  additionalFilters: WorkflowInstanceFilter,
  instanceFilters: WorkflowInstanceFilter,
  reportType?: string,
  expandedFields: ExpandedEmbeddedFields,
  expandedRows: ExpandedEmbeddedRows,
  formFieldsVisibility: FormFieldsVisibility,
  allRecords: AllRecords,
  _hideNewReportsModal: Function,
  _createReport: Function,
  _reportModalAttributes: Function,
  _updateReportTitle: Function,
  _deleteReport: Function,
};

const Content = ({
  _updateReportTitle,
  _deleteReport,
  filters,
  report,
  reportModal,
  processColumns,
  type,
  additionalFilters,
  reportType,
  instanceFilters,
  expandedFields,
  expandedRows,
  formFieldsVisibility,
  allRecords,
  _hideNewReportsModal,
  _createReport,
  _reportModalAttributes,
}: Props) => {
  const closeModal = useCallback(
    (event: any) => {
      if (event.key === "Escape") {
        _hideNewReportsModal();
      }
    },
    [_hideNewReportsModal],
  );

  useEffect(() => {
    document.addEventListener("keydown", closeModal, false);
    return () => {
      document.removeEventListener("keydown", closeModal, false);
    };
  }, []);

  const expansionState = {
    expandedEmbeddedFields: expandedFields,
    expandedEmbeddedRows: expandedRows,
    formFieldsVisibility,
  };

  const extractDeepFilter = () => {
    const deepFilter = [];
    Object.keys(allRecords).map((filterColumnId) => {
      if (allRecords[filterColumnId]) {
        deepFilter.push(filterColumnId);
      }
    });

    return deepFilter;
  };

  const handleSubmit = useCallback(
    (event: any) => {
      event.preventDefault();

      switch (type) {
        case "create":
          let request = {
            title: reportModal.title,
            ...(reportType ? { type: reportType } : {}),
            settings: {},
            filters: {
              ...filters,
              ...additionalFilters,
            },
          };

          if (
            reportType !== "form" &&
            processColumns &&
            processColumns.length > 0
          ) {
            request.settings = {
              columns: processColumns,
            };
          }
          const updatedFilters = {};
          for (const [key, value] of Object.entries(request.filters)) {
            if (key.includes("-")) {
              // Replacing because dashes in property names of embedded field filters
              // were being removed when saving in firestore
              const replaceWithArrow = key.split("-").join(">");
              updatedFilters[replaceWithArrow] = value;
            } else {
              updatedFilters[key] = value;
            }
          }

          const deepFilter = extractDeepFilter();
          request["filters"] = {
            ...updatedFilters,
            ...(deepFilter ? { deepFilter } : {}),
          };
          _createReport(request, expansionState);
          break;
        case "clone":
          if (report) {
            let request = {
              title: reportModal.title,
              settings: {},
              filters: {
                // Filters that are currently applied to the report
                ...instanceFilters,
              },
            };

            if (
              report.settings?.columns &&
              (report.settings?.columns || []).length > 0
            ) {
              request.settings = {
                columns: report.settings?.columns || [],
              };
            }
            const updatedFilters = {};
            for (const [key, value] of Object.entries(request.filters)) {
              if (key.includes("-")) {
                // Replacing because dashes in property names of embedded field filters
                // were being removed when saving in firestore
                const replaceWithArrow = key.split("-").join(">");
                updatedFilters[replaceWithArrow] = value;
              } else {
                updatedFilters[key] = value;
              }
            }
            const deepFilter = extractDeepFilter();

            request["filters"] = {
              ...updatedFilters,
              ...(deepFilter ? { deepFilter } : {}),
            };

            _createReport(request, expansionState);
          }
          break;
        case "update":
          _updateReportTitle();
          break;
        case "delete":
          _deleteReport({ reportId: reportModal.id });
          break;
        default:
          break;
      }
    },
    [filters, reportModal, _createReport, _updateReportTitle, _deleteReport],
  );

  const handleChange = useCallback(
    (event: any) => {
      _reportModalAttributes({ title: event.target.value });
    },
    [_reportModalAttributes],
  );

  return (
    <Portal>
      <Overlay>
        <StyledContent>
          <Form onSubmit={handleSubmit}>
            <HeadingWrapper>
              <Heading type={type} />
            </HeadingWrapper>
            <ModalFormBody>
              <ModalText>
                {type === "delete" ? (
                  <div>
                    {i18n.t(k.DO_YOU_WANT_TO_DELETE)}
                    <strong>{reportModal.title}</strong>
                    {i18n.t(k._10)}
                  </div>
                ) : (
                  <Input
                    value={reportModal.title}
                    onChange={handleChange}
                    placeholder={i18n.t(k.ENTER_REPORT_TITLE)}
                    autoFocus
                  />
                )}
              </ModalText>
            </ModalFormBody>
            <SecondaryFooter>
              <PrimaryButton delete={type === "delete"}>
                <ButtonLoader
                  type="submit"
                  disabled={reportModal.title.length === 0}
                  isLoading={reportModal.loading}
                  color={colors.danger}
                >
                  {type === "delete" ? i18n.t(k.DELETE) : i18n.t(k.SAVE)}
                </ButtonLoader>
              </PrimaryButton>
              <Secondary type="button" onClick={_hideNewReportsModal}>
                {i18n.t(k.CANCEL)}
              </Secondary>
            </SecondaryFooter>
          </Form>
        </StyledContent>
      </Overlay>
    </Portal>
  );
};

const mapStateToProps = ({ app }: { app: AppState }) => ({
  reportModal: getReportModal(app),
  filters: getWorkflowInstanceFilter(app),
  additionalFilters: getAdditionalFilters(app),
  instanceFilters: getWorkflowInstanceFilter(app),
  processColumns: getProcessTableColumns(app, getProcessFilterId(app) || ""),
  report: getReport(app, getInstanceReportId(app) || ""),
  reportType: getModalReportType(app),
  expandedFields: getExpandedFields(app),
  expandedRows: getExpandedRows(app),
  formFieldsVisibility: getFormFieldsVisibility(app),
  allRecords: getAllRecords(app),
});

export default connect(mapStateToProps, {
  _hideNewReportsModal: hideNewReportsModal,
  _createReport: createReport,
  _reportModalAttributes: reportModalAttributes,
  _updateReportTitle: updateReportTitle,
  _deleteReport: deleteReport,
})(Content);
